import { getCalcfocusToken } from "service/userservice/getCalcfocusToken";
import { useContext, useEffect, useRef, useState } from "react";
import { AgentData } from "context/Agent/DataProvider";
import { useTranslation, Trans } from "react-i18next";
import { Dialog } from "@headlessui/react";
import { useSelector } from "react-redux";

import GeneralPurposeDialog from "components/General/GeneralPurposeDialog";
import DialogBackdrop from "components/ModalDialogs/DialogBackdrop";
import useTrackIllustraion from "logging/hooks/useTrackIllustraion";
import config from "config/config";
import AgentAutoCompleteComboBox from "components/General/AutoCompleteComboBox/AgentAutoCompleteComboBox";

const PolicyLookUpFromComboBoxModal = ({
  isOpenPolicyLookup,
  setIsOpenPolicyLookup,
}) => {
  const navigateToCalcFocusform = useRef();
  const { t } = useTranslation();
  const { agentPolicies } = useContext(AgentData);
  const { user } = useSelector((s) => s.user);
  const [policyFilterOptions, setPolicyFilterOptions] = useState([]);
  const [isSelectedLifeSysPolicy, setIsSelectedLifeSysPolicy] = useState(false);
  const [selectedPolicyId, setSelectedPolicyId] = useState();
  const [calcfocusToken, setCalcfocusToken] = useState("");
  const trackIllustraion = useTrackIllustraion();
  const [isDisabled, setIsDisabled] = useState(true);

  const performSubmit = async () => {
    const policy = agentPolicies.find(
      (p) => p.oldPolicy === selectedPolicyId.value
    );
    if (policy?.sourceSystemName?.toUpperCase() === "CALCFOCUS") {
      let res = await getCalcfocusToken(user?.userId, policy?.oldPolicy);
      if (res?.token) {
        setCalcfocusToken(res?.token);
        setTimeout(() => {
          navigateToCalcFocusform.current.submit();
          trackIllustraion({
            ...user,
            provider: "Calcfocus",
          });
          setIsOpenPolicyLookup(false);
        }, 1);
      }
    } else {
      setIsSelectedLifeSysPolicy(true);
      setIsOpenPolicyLookup(false);
    }
    setSelectedPolicyId(policyFilterOptions[0]);
  };

  // getting policy number with primary owner full name
  useEffect(() => {
    if (agentPolicies.length > 0) {
      let policySelectionArray = [];
      agentPolicies?.map((policy) => {
        policySelectionArray.push({
          ...policy,
          value: policy?.oldPolicy,
          displayAsValue: policy.oldPolicy,
          displayAs: `${policy.oldPolicy}: ${policy?.insuredFullName}`,
        });
      });
      if (policySelectionArray?.length > 0)
        setPolicyFilterOptions([
          {
            value: "all",
            displayAs: "Select Contract",
            displayAsValue: "Select Contract",
          },
          ...policySelectionArray,
        ]);
    }
  }, [agentPolicies]);
  useEffect(() => {
    if (!selectedPolicyId || selectedPolicyId?.value === "all")
      setIsDisabled(true);
    else setIsDisabled(false);
  }, [selectedPolicyId, isOpenPolicyLookup]);
  return (
    <>
      <Dialog
        open={isOpenPolicyLookup}
        onClose={() => null}
        className="ui-dialogs"
      >
        <DialogBackdrop />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel
            role="dialog"
            aria-modal="true"
            className="ui-default-dialog overflow-visible"
          >
            <Dialog.Title className="font-body text-xl font-bold text-left">
              {t("AgentPolicyLookUp-search-contract-title")}
              <div className="text-xs text-theme-text-secondary font-normal mt-2">
                {t("AgentPolicyLookUp-search-contract-subtitle")}
              </div>
            </Dialog.Title>
            <button
              className="ui-default-dialog-close"
              aria-label={t(
                "AgentPolicyLookupModal-dialogClose-button-ariaLabel"
              )}
              onClick={() => {
                setSelectedPolicyId(policyFilterOptions[0]);
                setIsOpenPolicyLookup(false);
              }}
            >
              <svg
                className="ui-icon h-3.5 w-3.5"
                focusable="false"
                aria-hidden="true"
                role="presentation"
              >
                <use href="#ui-icon-close"></use>
              </svg>
            </button>
            <hr />
            <Dialog.Description>
              <span className="sr-only">
                {t("AgentPolicyLookupModal-dialogDescription-srText")}
              </span>
            </Dialog.Description>
            <div className="grid gap-2 md:gap-x-4 md:grid-cols-[2fr,0.5fr]">
              <AgentAutoCompleteComboBox
                labelName=""
                inputPlaceholderText={"Select Contract"}
                options={policyFilterOptions}
                invalidMessage={t("AlertsCorrespondence-input-invalid-msg-1")}
                selectedOption={selectedPolicyId}
                setSelectedOption={setSelectedPolicyId}
              />
              <div className="relative mt-1">
                <form
                  method="POST"
                  action={config.CALCFOCUS_SERVICE_BASE_URL}
                  target="_blank"
                  ref={navigateToCalcFocusform}
                >
                  <input type="hidden" name="jwtToken" value={calcfocusToken} />
                  <button
                    id="id_policyLookupBtn"
                    className="ui-btn-primary h-9 w-32 mx-auto ui-btn-min-w mt-4 sm:mt-0"
                    disabled={isDisabled}
                    type="button"
                    onClick={() => performSubmit()}
                  >
                    {t("AgentPolicyLookup-serach-button")}
                  </button>
                </form>
              </div>
            </div>
            <div className="block text-right mt-2">
              <button
                className="ui-btn-secondary ui-btn-min-w mt-4"
                onClick={() => {
                  setSelectedPolicyId(policyFilterOptions[0]);
                  setIsOpenPolicyLookup(false);
                }}
              >
                <Trans
                  i18nKey="AgentPolicyLookupModal-dialogClose-button-text"
                  components={{
                    span1: <span className="sr-only" />,
                  }}
                />
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
      <GeneralPurposeDialog
        state={isSelectedLifeSysPolicy}
        setState={setIsSelectedLifeSysPolicy}
        title={t("AgentPolicyLookUp-general-purpose-dialog-title")}
        desc={t("AgentPolicyLookUp-general-purpose-dialog-desc")}
        width="max-w-md"
        enableHrBlock={false}
        enablePrimaryButton={true}
        enableSecondaryButton={false}
        primaryButtonText={t("SecondaryButton-text")}
        primaryButtonAction={() => setIsSelectedLifeSysPolicy(false)}
      />
    </>
  );
};

export default PolicyLookUpFromComboBoxModal;
